import { useState, useCallback } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import {
  Upload, FileText, Loader2, Package, ShoppingCart, AlertTriangle,
  CheckCircle, Trash2, Edit, X, Sparkles, Camera, Image, Scale,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useWorkspace } from "@/hooks/useWorkspace";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

interface Supplier {
  id: string;
  name: string;
  contact_name: string | null;
}

interface ExtractedItem {
  description: string;
  sku: string | null;
  quantity: number;
  unit_price: number;
  discount: number | null;
  shipping: number | null;
  tax: number | null;
  line_total: number;
  // UI state
  selected: boolean;
  existingProductId: string | null;
}

interface ExtractedInvoice {
  invoice_number: string | null;
  invoice_date: string | null;
  supplier_name: string | null;
  supplier_tax_id: string | null;
  supplier_address: string | null;
  supplier_city: string | null;
  supplier_postal_code: string | null;
  supplier_country: string | null;
  supplier_phone: string | null;
  supplier_email: string | null;
  supplier_website: string | null;
  supplier_contact_name: string | null;
  payment_method: string | null;
  subtotal: number | null;
  shipping_cost: number | null;
  discount_total: number | null;
  tax_amount: number | null;
  equivalence_surcharge_rate: number | null;
  equivalence_surcharge_amount: number | null;
  total_amount: number | null;
  currency: string;
  items: ExtractedItem[];
  // Resolved supplier after AI processing
  resolved_supplier_id?: string | null;
  resolved_supplier_name?: string | null;
  supplier_is_new?: boolean;
  // Missing RE surcharge (manual flag)
  missing_equivalence_surcharge?: boolean;
  missing_surcharge_rate?: number;
  missing_surcharge_amount?: number;
  // IVA 309 expense (inversión sujeto pasivo / adquisición intracomunitaria)
  iva_309_expense?: boolean;
  iva_309_mode?: "" | "iva_re" | "solo_iva";
  iva_309_expense_rate?: number;
  iva_309_expense_amount?: number;
  iva_309_re_amount?: number;
}

interface PdfFile {
  file: File;
  status: "pending" | "processing" | "done" | "error";
  extractedData: ExtractedInvoice | null;
  error: string | null;
  isImage?: boolean;
}

interface InvoiceImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  suppliers: Supplier[];
  onOrderCreated: () => void;
}

export function InvoiceImportDialog({ open, onOpenChange, suppliers, onOrderCreated }: InvoiceImportDialogProps) {
  const [step, setStep] = useState<"upload" | "preview" | "creating">("upload");
  const [supplierId, setSupplierId] = useState("");
  const [pdfFiles, setPdfFiles] = useState<PdfFile[]>([]);
  const [activeFileIndex, setActiveFileIndex] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [creationProgress, setCreationProgress] = useState(0);
  const [initialStatus, setInitialStatus] = useState("pending");
  const { user } = useAuth();
  const { toast } = useToast();
  const { workspace } = useWorkspace();

  const selectedSupplier = suppliers.find(s => s.id === supplierId);

  const ACCEPTED_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/webp", "image/heic"];

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const newPdfFiles: PdfFile[] = files
      .filter(f => ACCEPTED_TYPES.includes(f.type) || f.type.startsWith("image/"))
      .map(f => ({
        file: f,
        status: "pending" as const,
        extractedData: null,
        error: null,
        isImage: f.type.startsWith("image/"),
      }));
    setPdfFiles(prev => [...prev, ...newPdfFiles]);
  };

  const handleCameraCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const newFiles: PdfFile[] = files.map(f => ({
      file: f,
      status: "pending" as const,
      extractedData: null,
      error: null,
      isImage: true,
    }));
    setPdfFiles(prev => [...prev, ...newFiles]);
  };

  const removePdfFile = (index: number) => {
    setPdfFiles(prev => prev.filter((_, i) => i !== index));
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const extractStructuredPdfText = (textContent: any, pageNumber: number) => {
    const positionedItems = (textContent?.items || [])
      .filter((item: any) => typeof item?.str === "string" && Array.isArray(item?.transform) && item.transform.length >= 6)
      .map((item: any) => ({
        text: item.str.replace(/\s+/g, " ").trim(),
        x: Number(item.transform[4] || 0),
        y: Number(item.transform[5] || 0),
        width: Number(item.width || 0),
      }))
      .filter((item: any) => item.text.length > 0);

    if (positionedItems.length === 0) {
      return { plainText: "", reconstructedRowsText: "" };
    }

    const Y_TOLERANCE = 3;
    const rowsMap = new Map<number, Array<{ text: string; x: number; y: number; width: number }>>();

    for (const item of positionedItems) {
      const roundedY = Math.round(item.y / Y_TOLERANCE) * Y_TOLERANCE;
      const rowKey = Array.from(rowsMap.keys()).find((key) => Math.abs(key - roundedY) <= Y_TOLERANCE) ?? roundedY;
      const currentRow = rowsMap.get(rowKey) || [];
      currentRow.push(item);
      rowsMap.set(rowKey, currentRow);
    }

    const sortedRows = Array.from(rowsMap.entries())
      .sort((a, b) => b[0] - a[0])
      .map(([, rowItems]) => rowItems.sort((a, b) => a.x - b.x));

    const plainRows = sortedRows
      .map((row) => row.map((item) => item.text).join(" ").replace(/\s+/g, " ").trim())
      .filter(Boolean);

    const reconstructedRows = sortedRows
      .map((row) => {
        return row.reduce((line, item, index) => {
          if (index === 0) return item.text;

          const previous = row[index - 1];
          const previousRightEdge = previous.x + Math.max(previous.width, previous.text.length * 4.5);
          const gap = item.x - previousRightEdge;
          const separator = gap > 24 ? " | " : " ";

          return `${line}${separator}${item.text}`;
        }, "").replace(/\s+/g, " ").trim();
      })
      .filter(Boolean);

    const candidateLineRows = reconstructedRows.filter((row) => {
      const normalized = row.toLowerCase();
      const hasWords = /[a-záéíóúñü]{3,}/i.test(row);
      const hasNumbers = /\d/.test(row);
      const hasCommercialSignals = /(€|usd|eur|qty|cantidad|units?|pcs|pieza|ref\.?|sku|item|modelo|color|size|talla|\b\d+[.,]\d{2}\b|\b\d+\s*x\s*\d+[.,]\d{2}\b)/i.test(row);
      const looksLikeSummary = /(subtotal|total|base imponible|iva|recargo|equivalence|descuento total|importe total|payment|método de pago)/i.test(normalized);
      return hasWords && hasNumbers && hasCommercialSignals && !looksLikeSummary;
    });

    const dedupedRows = Array.from(new Set(candidateLineRows.map((row) => row.replace(/\s+/g, " ").trim())));

    return {
      plainText: plainRows.length ? `Página ${pageNumber}:\n${plainRows.join("\n")}` : "",
      reconstructedRowsText: dedupedRows.length
        ? `Página ${pageNumber} - filas reconstruidas:\n${dedupedRows.map((row) => `[ROW] ${row}`).join("\n")}`
        : "",
    };
  };

  const compressCanvasToJpeg = (sourceCanvas: HTMLCanvasElement, quality = 0.52, maxDimension = 1100) => {
    const longestSide = Math.max(sourceCanvas.width, sourceCanvas.height);

    if (longestSide <= maxDimension) {
      return sourceCanvas.toDataURL("image/jpeg", quality);
    }

    const scale = maxDimension / longestSide;
    const resizedCanvas = document.createElement("canvas");
    resizedCanvas.width = Math.max(1, Math.round(sourceCanvas.width * scale));
    resizedCanvas.height = Math.max(1, Math.round(sourceCanvas.height * scale));

    const resizedContext = resizedCanvas.getContext("2d");
    if (!resizedContext) {
      return sourceCanvas.toDataURL("image/jpeg", quality);
    }

    resizedContext.drawImage(sourceCanvas, 0, 0, resizedCanvas.width, resizedCanvas.height);
    return resizedCanvas.toDataURL("image/jpeg", quality);
  };

  const pdfToAiPayload = async (file: File): Promise<{ imageBase64List: string[]; extractedText: string; reconstructedRowsText: string }> => {
    const pdfData = new Uint8Array(await file.arrayBuffer());
    const loadingTask = pdfjsLib.getDocument({ data: pdfData, useWorkerFetch: false });
    const pdf = await loadingTask.promise;
    const maxPages = Math.min(pdf.numPages, 4);
    const imageBase64List: string[] = [];
    const textChunks: string[] = [];
    const reconstructedRowsChunks: string[] = [];

    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
      const page = await pdf.getPage(pageNumber);

      if (pageNumber <= maxPages) {
        const viewport = page.getViewport({ scale: 1.1 });
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");

        if (!context) {
          throw new Error("No se pudo preparar el PDF para su análisis");
        }

        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);

        await page.render({
          canvas,
          canvasContext: context,
          viewport,
        }).promise;

        imageBase64List.push(compressCanvasToJpeg(canvas, 0.5, 1050));
      }

      const textContent = await page.getTextContent();
      const { plainText, reconstructedRowsText } = extractStructuredPdfText(textContent, pageNumber);

      if (plainText) {
        textChunks.push(plainText);
      }

      if (reconstructedRowsText) {
        reconstructedRowsChunks.push(reconstructedRowsText);
      }
    }

    return {
      imageBase64List,
      extractedText: textChunks.length
        ? `TEXTO COMPLETO EXTRAÍDO DEL PDF:\n${textChunks.join("\n\n")}`.slice(0, 60000)
        : "",
      reconstructedRowsText: reconstructedRowsChunks.join("\n\n").slice(0, 40000),
    };
  };

  const fileToAiPayload = async (file: File): Promise<{ imageBase64List?: string[]; extractedText?: string; reconstructedRowsText?: string; pdfBase64?: string }> => {
    if (file.type === "application/pdf") {
      return pdfToAiPayload(file);
    }

    return {
      pdfBase64: await fileToBase64(file),
    };
  };

  const estimatePayloadSize = (payload: Record<string, unknown>) => {
    try {
      return new Blob([JSON.stringify(payload)]).size;
    } catch {
      return 0;
    }
  };

  const buildPayloadVariants = (payload: { imageBase64List?: string[]; extractedText?: string; reconstructedRowsText?: string; pdfBase64?: string }) => {
    if (payload.pdfBase64) {
      return [payload];
    }

    const imageBase64List = payload.imageBase64List || [];
    const extractedText = payload.extractedText || "";
    const reconstructedRowsText = payload.reconstructedRowsText || "";

    const variants = [
      {
        ...payload,
      },
      {
        imageBase64List: imageBase64List.slice(0, 3),
        extractedText: extractedText.slice(0, 40000),
        reconstructedRowsText: reconstructedRowsText.slice(0, 24000),
      },
      {
        imageBase64List: imageBase64List.slice(0, 2),
        extractedText: extractedText.slice(0, 18000),
        reconstructedRowsText: reconstructedRowsText.slice(0, 18000),
      },
      {
        imageBase64List: imageBase64List.slice(0, 1),
        extractedText: "",
        reconstructedRowsText: reconstructedRowsText.slice(0, 16000),
      },
    ];

    return variants.filter((variant, index, all) => {
      if (!variant.imageBase64List?.length && !variant.pdfBase64) return false;
      const signature = JSON.stringify(variant);
      return all.findIndex((candidate) => JSON.stringify(candidate) === signature) === index;
    });
  };

  // Normalize tax ID: strip whitespace, dashes, dots, and ES/country prefix
  const normalizeTaxId = (taxId: string): string => {
    let clean = taxId.replace(/[\s.\-/]/g, "").toUpperCase();
    // Strip 2-letter country prefix (e.g. ES, FR, DE)
    if (/^[A-Z]{2}[A-Z0-9]/.test(clean) && clean.length > 9) {
      clean = clean.slice(2);
    }
    return clean;
  };

  const matchSupplierByTaxId = async (rawTaxId: string) => {
    const normalized = normalizeTaxId(rawTaxId);
    // Search with multiple patterns to handle ES prefix variations
    const patterns = [normalized, `ES${normalized}`, `%${normalized}`];
    for (const pattern of patterns) {
      const { data } = await supabase
        .from("suppliers")
        .select("id, name")
        .eq("workspace_id", workspace?.id)
        .ilike("tax_id", pattern)
        .maybeSingle();
      if (data) return data;
    }
    return null;
  };

  const resolveSupplier = async (extracted: ExtractedInvoice): Promise<ExtractedInvoice> => {
    // If user manually selected a supplier, use that
    if (supplierId) {
      const selected = suppliers.find(s => s.id === supplierId);
      return {
        ...extracted,
        resolved_supplier_id: supplierId,
        resolved_supplier_name: selected?.name || extracted.supplier_name,
        supplier_is_new: false,
      };
    }

    // Try to match by tax_id first (with normalized matching)
    if (extracted.supplier_tax_id) {
      const existing = await matchSupplierByTaxId(extracted.supplier_tax_id);
      if (existing) {
        return {
          ...extracted,
          resolved_supplier_id: existing.id,
          resolved_supplier_name: existing.name,
          supplier_is_new: false,
        };
      }
    }

    // Try to match by name (fuzzy)
    if (extracted.supplier_name) {
      const { data: existingByName } = await supabase
        .from("suppliers")
        .select("id, name")
        .eq("workspace_id", workspace?.id)
        .ilike("name", `%${extracted.supplier_name.trim()}%`)
        .maybeSingle();

      if (existingByName) {
        return {
          ...extracted,
          resolved_supplier_id: existingByName.id,
          resolved_supplier_name: existingByName.name,
          supplier_is_new: false,
        };
      }
    }

    // Supplier not found — will create automatically when creating the order
    return {
      ...extracted,
      resolved_supplier_id: null,
      resolved_supplier_name: extracted.supplier_name,
      supplier_is_new: true,
    };
  };

  const createSupplierFromInvoice = async (inv: ExtractedInvoice): Promise<string | null> => {
    if (!inv.supplier_name) return null;

    const { data: newSupplier, error } = await supabase
      .from("suppliers")
      .insert({
        name: inv.supplier_name,
        tax_id: inv.supplier_tax_id?.replace(/[\s.-]/g, "").toUpperCase() || null,
        address: inv.supplier_address || null,
        city: inv.supplier_city || null,
        country: inv.supplier_country || "España",
        phone: inv.supplier_phone || null,
        email: inv.supplier_email || null,
        website: inv.supplier_website || null,
        contact_name: inv.supplier_contact_name || null,
        status: "active",
        created_by: user?.id,
      } as any)
      .select("id")
      .single();

    if (error) {
      console.error("Error creating supplier:", error);
      return null;
    }

    toast({
      title: "Proveedor creado",
      description: `Se ha creado automáticamente el proveedor "${inv.supplier_name}"`,
    });

    return newSupplier.id;
  };

  const processAllFiles = async () => {
    if (pdfFiles.length === 0) {
      toast({ title: "Error", description: "Sube al menos un archivo", variant: "destructive" });
      return;
    }

    setProcessing(true);

    for (let i = 0; i < pdfFiles.length; i++) {
      if (pdfFiles[i].status === "done") continue;

      setPdfFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: "processing" } : f));

      try {
        const payload = await fileToAiPayload(pdfFiles[i].file);
        const payloadVariants = buildPayloadVariants(payload);

        let data: any = null;
        let lastError: Error | null = null;

        for (const variant of payloadVariants) {
          const requestBody = {
            ...variant,
            supplierName: selectedSupplier?.name || "",
          };

          try {
            console.log("Enviando factura a IA", {
              variantImages: variant.imageBase64List?.length || 0,
              extractedTextLength: variant.extractedText?.length || 0,
              reconstructedRowsLength: variant.reconstructedRowsText?.length || 0,
              estimatedBytes: estimatePayloadSize(requestBody),
            });

            const response = await supabase.functions.invoke("process-invoice-pdf", {
              body: requestBody,
            });

            if (response.error) {
              throw new Error(response.error.message);
            }

            if (response.data?.error) {
              throw new Error(response.data.error);
            }

            data = response.data;
            lastError = null;
            break;
          } catch (invokeError: any) {
            lastError = invokeError instanceof Error ? invokeError : new Error(invokeError?.message || "Error procesando el archivo");

            const message = String(lastError.message || "").toLowerCase();
            const shouldRetryReducedPayload = message.includes("failed to send request to the edge function")
              || message.includes("fetch")
              || message.includes("network")
              || message.includes("request entity too large")
              || message.includes("edge function returned a non-2xx status code");

            if (!shouldRetryReducedPayload) {
              throw lastError;
            }
          }
        }

        if (lastError) throw lastError;
        if (data?.error) throw new Error(data.error);

        let extracted: ExtractedInvoice = data.data;
        // Initialize UI state for items
        extracted.items = (extracted.items || []).map(item => ({
          ...item,
          selected: true,
          existingProductId: null,
          quantity: item.quantity || 1,
          unit_price: item.unit_price || 0,
          discount: item.discount || 0,
          shipping: item.shipping || 0,
          tax: item.tax || 0,
          line_total: item.line_total || 0,
        }));

        // Auto-resolve supplier
        extracted = await resolveSupplier(extracted);

        setPdfFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: "done", extractedData: extracted } : f));
      } catch (err: any) {
        setPdfFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: "error", error: err.message } : f));
      }
    }

    setProcessing(false);
    setStep("preview");
    setActiveFileIndex(0);
  };

  const updateItem = (fileIdx: number, itemIdx: number, field: string, value: any) => {
    setPdfFiles(prev => prev.map((f, fi) => {
      if (fi !== fileIdx || !f.extractedData) return f;
      const items = [...f.extractedData.items];
      items[itemIdx] = { ...items[itemIdx], [field]: value };
      return { ...f, extractedData: { ...f.extractedData, items } };
    }));
  };

  const updateInvoiceField = (fileIdx: number, field: string, value: any) => {
    updateInvoiceFields(fileIdx, { [field]: value });
  };

  const updateInvoiceFields = (fileIdx: number, fields: Record<string, any>) => {
    setPdfFiles(prev => prev.map((f, fi) => {
      if (fi !== fileIdx || !f.extractedData) return f;
      return { ...f, extractedData: { ...f.extractedData, ...fields } };
    }));
  };

  const generateSku = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let result = "IMP-";
    for (let i = 0; i < 6; i++) result += chars.charAt(Math.floor(Math.random() * chars.length));
    return result;
  };

  const createOrdersFromInvoices = async () => {
    setCreating(true);
    setStep("creating");
    setCreationProgress(0);

    const completedFiles = pdfFiles.filter(f => f.status === "done" && f.extractedData);
    let created = 0;

    // Cache of suppliers created during this batch to avoid duplicates
    // Key: normalized tax_id or lowercase name, Value: supplier id
    const createdSuppliersCache = new Map<string, string>();

    for (const pdfFile of completedFiles) {
      const inv = pdfFile.extractedData!;
      const selectedItems = inv.items.filter(item => item.selected);

      if (selectedItems.length === 0) {
        created++;
        setCreationProgress(Math.round((created / completedFiles.length) * 100));
        continue;
      }

      try {
        // 0. Resolve supplier: create if new, but check batch cache first
        let finalSupplierId = inv.resolved_supplier_id || null;
        if (inv.supplier_is_new && !finalSupplierId) {
          // Check if we already created this supplier in a previous iteration
          const cleanTaxId = inv.supplier_tax_id ? normalizeTaxId(inv.supplier_tax_id) : null;
          const cacheKeyTax = cleanTaxId ? `tax:${cleanTaxId}` : null;
          const cacheKeyName = inv.supplier_name ? `name:${inv.supplier_name.toLowerCase().trim()}` : null;

          const cachedId = (cacheKeyTax && createdSuppliersCache.get(cacheKeyTax))
            || (cacheKeyName && createdSuppliersCache.get(cacheKeyName))
            || null;

          if (cachedId) {
            finalSupplierId = cachedId;
          } else {
            // Also re-check DB using normalized tax_id matching
            if (cleanTaxId) {
              const existing = await matchSupplierByTaxId(inv.supplier_tax_id!);
              if (existing) finalSupplierId = existing.id;
            }
            if (!finalSupplierId && inv.supplier_name) {
              const { data: recheck } = await supabase
                .from("suppliers")
                .select("id")
                .eq("workspace_id", workspace?.id)
                .ilike("name", `%${inv.supplier_name.trim()}%`)
                .maybeSingle();
              if (recheck) finalSupplierId = recheck.id;
            }

            if (!finalSupplierId) {
              finalSupplierId = await createSupplierFromInvoice(inv);
            }

            // Store in cache for subsequent invoices
            if (finalSupplierId) {
              if (cacheKeyTax) createdSuppliersCache.set(cacheKeyTax, finalSupplierId);
              if (cacheKeyName) createdSuppliersCache.set(cacheKeyName, finalSupplierId);
            }
          }
        }

        // 1. Check for duplicate invoice number FIRST (before creating products)
        if (inv.invoice_number) {
          let duplicateQuery = supabase
            .from("orders")
            .select("id, order_number")
            .eq("supplier_invoice_number", inv.invoice_number);

          // Also filter by supplier if available for more precise matching
          if (finalSupplierId) {
            duplicateQuery = duplicateQuery.eq("supplier_id", finalSupplierId);
          }

          const { data: existingOrder } = await duplicateQuery.maybeSingle();

          if (existingOrder) {
            toast({
              title: "Factura duplicada",
              description: `Ya existe una orden (${existingOrder.order_number}) con el nº de factura "${inv.invoice_number}". Se ha omitido para evitar duplicados.`,
              variant: "destructive",
            });
            created++;
            setCreationProgress(Math.round((created / completedFiles.length) * 100));
            continue;
          }
        } else {
          // Fallback: check by supplier + date + total to catch duplicates without invoice number
          if (finalSupplierId && inv.invoice_date && inv.total_amount) {
            const { data: existingByMeta } = await supabase
              .from("orders")
              .select("id, order_number")
              .eq("supplier_id", finalSupplierId)
              .eq("order_date", inv.invoice_date)
              .eq("total", inv.total_amount)
              .eq("order_type", "purchase")
              .maybeSingle();

            if (existingByMeta) {
              toast({
                title: "Posible factura duplicada",
                description: `Ya existe una orden (${existingByMeta.order_number}) del mismo proveedor, fecha e importe. Se ha omitido.`,
                variant: "destructive",
              });
              created++;
              setCreationProgress(Math.round((created / completedFiles.length) * 100));
              continue;
            }
          }
        }

        // 2. Create or find products for ALL items (check by SKU or name to avoid duplicates)
        const orderItems: { product_id: string; quantity: number; unit_price: number; total: number; discount: number }[] = [];
        // Track products created/found in this batch to avoid collisions within the same invoice
        const batchCache = new Map<string, string>(); // key -> productId

        // Helper: check if SKU is meaningful (not generic placeholders)
        const isMeaningfulSku = (sku: string | null): boolean => {
          if (!sku) return false;
          const generic = ["n/a", "na", "-", "sin", "none", "null", "undefined", ""];
          return !generic.includes(sku.trim().toLowerCase());
        };

        const upsertInventory = async (productId: string, qty: number) => {
          const { data: existingInv } = await supabase
            .from("inventory")
            .select("id, quantity")
            .eq("product_id", productId)
            .maybeSingle();
          if (existingInv) {
            await supabase
              .from("inventory")
              .update({ quantity: (existingInv.quantity || 0) + qty, last_movement_date: new Date().toISOString() })
              .eq("id", existingInv.id);
          } else {
            await supabase
              .from("inventory")
              .insert({ product_id: productId, quantity: qty, reserved_quantity: 0, warehouse: "Principal", last_movement_date: new Date().toISOString() });
          }
        };

        console.log(`Processing ${selectedItems.length} items from invoice...`);

        for (let itemIdx = 0; itemIdx < selectedItems.length; itemIdx++) {
          const item = selectedItems[itemIdx];
          let productId = item.existingProductId;

          try {
            const hasSku = isMeaningfulSku(item.sku);
            const cacheKey = hasSku ? `sku:${item.sku!.trim().toUpperCase()}` : `name:${item.description.trim().toLowerCase()}`;

            // Check batch cache first
            if (!productId) {
              const cachedId = batchCache.get(cacheKey);
              if (cachedId) {
                productId = cachedId;
                // Just add to inventory for extra quantity
                await upsertInventory(productId, item.quantity);
                // Update cost price
                await supabase.from("products").update({ cost_price: item.unit_price }).eq("id", productId);
              }
            }

            // Search in DB by SKU
            if (!productId && hasSku) {
              const { data: existingProduct } = await supabase
                .from("products")
                .select("id")
                .eq("sku", item.sku!)
                .maybeSingle();

              if (existingProduct) {
                productId = existingProduct.id;
                await upsertInventory(productId, item.quantity);
                await supabase.from("products").update({ cost_price: item.unit_price }).eq("id", productId);
              }
            }

            // Search in DB by name (when no SKU or SKU not found)
            if (!productId && item.description) {
              const { data: existingByName } = await supabase
                .from("products")
                .select("id")
                .ilike("name", item.description.trim())
                .maybeSingle();

              if (existingByName) {
                productId = existingByName.id;
                await upsertInventory(productId, item.quantity);
                await supabase.from("products").update({ cost_price: item.unit_price }).eq("id", productId);
              }
            }

            // Create new product if not found anywhere
            if (!productId) {
              const sku = hasSku ? item.sku! : generateSku();
              const { data: newProduct, error: prodError } = await supabase
                .from("products")
                .insert({
                  name: item.description,
                  sku,
                  cost_price: item.unit_price,
                  unit_price: 0,
                  status: "active",
                  is_shop_active: false,
                  created_by: user?.id,
                  supplier_id: finalSupplierId,
                  purchase_date: inv.invoice_date ? new Date(inv.invoice_date).toISOString() : new Date().toISOString(),
                } as any)
                .select("id")
                .single();

              if (prodError) {
                console.error(`Error creating product "${item.description}":`, prodError);
                // Retry with fallback SKU
                const fallbackSku = generateSku();
                const { data: retryProduct, error: retryError } = await supabase
                  .from("products")
                  .insert({
                    name: item.description,
                    sku: fallbackSku,
                    cost_price: item.unit_price,
                    unit_price: 0,
                    status: "active",
                    is_shop_active: false,
                    created_by: user?.id,
                    supplier_id: finalSupplierId,
                    purchase_date: inv.invoice_date ? new Date(inv.invoice_date).toISOString() : new Date().toISOString(),
                  } as any)
                  .select("id")
                  .single();

                if (retryError) {
                  console.error(`Error creating product (retry) "${item.description}":`, retryError);
                  continue;
                }
                productId = retryProduct.id;
              } else {
                productId = newProduct.id;
              }

              // Create initial inventory record
              await upsertInventory(productId, item.quantity);
              console.log(`Created product "${item.description}" with id ${productId}`);
            }

            // Cache for next items in this invoice
            batchCache.set(cacheKey, productId);

            orderItems.push({
              product_id: productId,
              quantity: item.quantity,
              unit_price: item.unit_price,
              total: item.line_total,
              discount: item.discount || 0,
            });

            console.log(`Item ${itemIdx + 1}/${selectedItems.length} processed: "${item.description}" -> product ${productId}`);
          } catch (itemErr) {
            console.error(`Error processing item ${itemIdx + 1} "${item.description}":`, itemErr);
          }
        }

        console.log(`Total order items to insert: ${orderItems.length} of ${selectedItems.length}`);

        // 3. Create order
        const orderNumber = `ORD-${new Date().toISOString().slice(2, 10).replace(/-/g, "")}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;

        // Upload the PDF/image to storage
        let invoiceFilePath: string | null = null;
        const uploadFormData = new FormData();
        uploadFormData.append("file", pdfFile.file);
        const { data: uploadResult, error: uploadError } = await supabase.functions.invoke("upload-order-invoice", {
          body: uploadFormData,
        });
        
        if (uploadError || uploadResult?.error) {
          console.error("Error uploading invoice file:", uploadError || uploadResult?.error);
          toast({
            title: "Aviso",
            description: `No se pudo guardar el archivo adjunto: ${uploadError?.message || uploadResult?.error}`,
          });
        } else {
          invoiceFilePath = uploadResult?.path;
          console.log("Invoice file uploaded successfully:", invoiceFilePath);
        }

        // Determine tax_type: if equivalence surcharge exists, use iva_req
        const hasSurcharge = inv.equivalence_surcharge_amount && inv.equivalence_surcharge_amount > 0;
        const taxType = hasSurcharge ? 'iva_req' : 'iva';
        // If iva+req, tax_amount should include both IVA + RE
        const totalTaxAmount = hasSurcharge 
          ? (inv.tax_amount || 0) + (inv.equivalence_surcharge_amount || 0)
          : (inv.tax_amount || 0);

        const { data: newOrder, error: orderError } = await supabase
          .from("orders")
          .insert({
            order_number: orderNumber,
            order_type: "purchase",
            status: initialStatus,
            subtotal: inv.subtotal || 0,
            tax_amount: totalTaxAmount,
            total: inv.total_amount || 0,
            currency: inv.currency || "EUR",
            notes: `Importado desde factura: ${inv.invoice_number || pdfFile.file.name}. Proveedor: ${inv.resolved_supplier_name || inv.supplier_name || 'Desconocido'}. Envío: ${inv.shipping_cost || 0}€. Descuento: ${inv.discount_total || 0}€.${hasSurcharge ? ` RE: ${inv.equivalence_surcharge_amount}€ (${inv.equivalence_surcharge_rate}%).` : ''}`,
            order_date: inv.invoice_date || new Date().toISOString().split("T")[0],
            created_by: user?.id,
            invoice_file_path: invoiceFilePath,
            supplier_invoice_number: inv.invoice_number,
            supplier_id: finalSupplierId,
            payment_method: inv.payment_method || null,
            tax_type: taxType,
            missing_equivalence_surcharge: inv.missing_equivalence_surcharge || false,
            missing_surcharge_rate: inv.missing_surcharge_rate || 0,
            missing_surcharge_amount: inv.missing_surcharge_amount || 0,
            include_in_model_309: (inv.missing_equivalence_surcharge || inv.iva_309_expense) || false,
            iva_309_expense: inv.iva_309_expense || false,
            iva_309_mode: inv.iva_309_mode || null,
            iva_309_expense_rate: inv.iva_309_expense_rate || 0,
            iva_309_expense_amount: inv.iva_309_expense_amount || 0,
            iva_309_re_amount: inv.iva_309_re_amount || 0,
          } as any)
          .select("id")
          .single();

        if (orderError) throw orderError;

        // 3. Insert order items
        const itemsToInsert = orderItems.map(item => ({
          order_id: newOrder.id,
          product_id: item.product_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total: item.total,
          discount: item.discount,
        }));

        const { error: itemsError } = await supabase.from("order_items").insert(itemsToInsert);
        if (itemsError) console.error("Error inserting items:", itemsError);

      } catch (err: any) {
        console.error("Error creating order from invoice:", err);
        toast({
          title: "Error",
          description: `Error procesando ${pdfFile.file.name}: ${err.message}`,
          variant: "destructive",
        });
      }

      created++;
      setCreationProgress(Math.round((created / completedFiles.length) * 100));
    }

    setCreating(false);
    toast({ title: "Importación completada", description: `${created} orden(es) creada(s) correctamente` });
    onOrderCreated();
    handleClose();
  };

  const handleClose = () => {
    setStep("upload");
    setSupplierId("");
    setPdfFiles([]);
    setActiveFileIndex(0);
    setProcessing(false);
    setCreating(false);
    setCreationProgress(0);
    onOpenChange(false);
  };

  const activeFile = pdfFiles[activeFileIndex];
  const completedCount = pdfFiles.filter(f => f.status === "done").length;
  const hasProcessedFiles = completedCount > 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-card border-border max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Importar Facturas
          </DialogTitle>
          <DialogDescription>
            Sube facturas (PDF o foto) de proveedores y crea órdenes y productos automáticamente con IA
          </DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-2 text-xs">
          <Badge variant={step === "upload" ? "default" : "secondary"} className="gap-1">
            1. Subir PDFs
          </Badge>
          <span className="text-muted-foreground">→</span>
          <Badge variant={step === "preview" ? "default" : "secondary"} className="gap-1">
            2. Revisar datos
          </Badge>
          <span className="text-muted-foreground">→</span>
          <Badge variant={step === "creating" ? "default" : "secondary"} className="gap-1">
            3. Crear órdenes
          </Badge>
        </div>

        {/* Step: Upload */}
        {step === "upload" && (
          <div className="space-y-4">
            {/* Supplier selection (optional) */}
            <div className="rounded-lg border border-border bg-card p-4 space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                <ShoppingCart className="h-4 w-4" /> Configuración
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs">Proveedor (opcional)</Label>
                  <Select value={supplierId} onValueChange={setSupplierId}>
                    <SelectTrigger className="h-9 text-sm bg-secondary border-border">
                      <SelectValue placeholder="Auto-detectar de la factura" />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      {suppliers.map(s => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name} {s.contact_name && `(${s.contact_name})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Si no seleccionas proveedor, se detectará automáticamente de la factura
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Estado inicial de la orden</Label>
                  <Select value={initialStatus} onValueChange={setInitialStatus}>
                    <SelectTrigger className="h-9 text-sm bg-secondary border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      <SelectItem value="pending">Pendiente</SelectItem>
                      <SelectItem value="confirmed">Confirmado</SelectItem>
                      <SelectItem value="delivered">Recibida</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* File upload area */}
            <div className="rounded-lg border border-border bg-card p-4 space-y-4">
               <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                <Upload className="h-4 w-4" /> Facturas (PDF o Imagen)
              </h3>
              <div className="flex flex-col sm:flex-row gap-3">
                <label className="flex-1 flex flex-col items-center justify-center border-2 border-dashed border-border rounded-lg p-6 cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors">
                  <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                  <p className="text-sm font-medium">Subir archivos</p>
                  <p className="text-xs text-muted-foreground mt-1">PDF, JPG, PNG, WebP</p>
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,image/*" multiple className="hidden" onChange={handleFilesSelected} />
                </label>
                <label className="flex flex-col items-center justify-center border-2 border-dashed border-border rounded-lg p-6 cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors sm:w-48">
                  <Camera className="h-8 w-8 text-muted-foreground mb-2" />
                  <p className="text-sm font-medium">Cámara</p>
                  <p className="text-xs text-muted-foreground mt-1">Capturar foto</p>
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleCameraCapture} />
                </label>
              </div>

              {/* File list */}
              {pdfFiles.length > 0 && (
                <div className="space-y-2">
                  {pdfFiles.map((pf, i) => (
                    <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-secondary/50 border border-border">
                      {pf.isImage ? (
                        <Image className={cn(
                          "h-5 w-5 shrink-0",
                          pf.status === "done" && "text-green-500",
                          pf.status === "error" && "text-destructive",
                          pf.status === "processing" && "text-primary animate-pulse",
                          pf.status === "pending" && "text-muted-foreground",
                        )} />
                      ) : (
                        <FileText className={cn(
                          "h-5 w-5 shrink-0",
                          pf.status === "done" && "text-green-500",
                          pf.status === "error" && "text-destructive",
                          pf.status === "processing" && "text-primary animate-pulse",
                          pf.status === "pending" && "text-muted-foreground",
                        )} />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{pf.file.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {(pf.file.size / 1024).toFixed(0)} KB
                          {pf.status === "processing" && " — Procesando con IA..."}
                          {pf.status === "done" && ` — ${pf.extractedData?.items.length || 0} productos detectados`}
                          {pf.status === "error" && ` — Error: ${pf.error}`}
                        </p>
                      </div>
                      {pf.status === "processing" && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
                      {pf.status === "done" && <CheckCircle className="h-4 w-4 text-green-500" />}
                      {pf.status === "error" && <AlertTriangle className="h-4 w-4 text-destructive" />}
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removePdfFile(i)} disabled={pf.status === "processing"}>
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>Cancelar</Button>
              <Button onClick={processAllFiles} disabled={processing || pdfFiles.length === 0}>
                {processing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Procesar con IA ({pdfFiles.filter(f => f.status !== "done").length} archivo{pdfFiles.filter(f => f.status !== "done").length !== 1 ? "s" : ""})
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Step: Preview */}
        {step === "preview" && (
          <div className="space-y-4">
            {/* File tabs */}
            {pdfFiles.length > 1 && (
              <div className="flex gap-1 overflow-x-auto pb-1">
                {pdfFiles.map((pf, i) => (
                  <Button
                    key={i}
                    variant={activeFileIndex === i ? "default" : "outline"}
                    size="sm"
                    className="shrink-0 text-xs"
                    onClick={() => setActiveFileIndex(i)}
                    disabled={pf.status !== "done"}
                  >
                    <FileText className="h-3.5 w-3.5 mr-1" />
                    {pf.file.name.substring(0, 20)}
                    {pf.status === "error" && <AlertTriangle className="h-3 w-3 ml-1 text-destructive" />}
                  </Button>
                ))}
              </div>
            )}

            {activeFile?.extractedData && (
              <>
                {/* Supplier detection info */}
                <div className={cn(
                  "rounded-lg border p-3 flex items-start gap-3",
                  activeFile.extractedData.supplier_is_new
                    ? "border-primary/50 bg-primary/5"
                    : "border-border bg-secondary/30"
                )}>
                  <div className="shrink-0 mt-0.5">
                    {activeFile.extractedData.supplier_is_new ? (
                      <Sparkles className="h-4 w-4 text-primary" />
                    ) : (
                      <CheckCircle className="h-4 w-4 text-primary" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">
                      {activeFile.extractedData.supplier_is_new
                        ? `Nuevo proveedor: "${activeFile.extractedData.supplier_name || 'Desconocido'}"`
                        : `Proveedor existente: "${activeFile.extractedData.resolved_supplier_name}"`
                      }
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {activeFile.extractedData.supplier_is_new
                        ? `Se creará automáticamente${activeFile.extractedData.supplier_tax_id ? ` (CIF/NIF: ${activeFile.extractedData.supplier_tax_id})` : ""}`
                        : "Vinculado automáticamente por CIF/NIF o nombre"
                      }
                    </p>
                    {activeFile.extractedData.supplier_is_new && (activeFile.extractedData.supplier_address || activeFile.extractedData.supplier_phone || activeFile.extractedData.supplier_email) && (
                      <div className="flex flex-wrap gap-2 mt-1">
                        {activeFile.extractedData.supplier_address && (
                          <span className="text-xs text-muted-foreground">📍 {activeFile.extractedData.supplier_address}{activeFile.extractedData.supplier_city ? `, ${activeFile.extractedData.supplier_city}` : ""}</span>
                        )}
                        {activeFile.extractedData.supplier_phone && (
                          <span className="text-xs text-muted-foreground">📞 {activeFile.extractedData.supplier_phone}</span>
                        )}
                        {activeFile.extractedData.supplier_email && (
                          <span className="text-xs text-muted-foreground">✉️ {activeFile.extractedData.supplier_email}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Invoice general data */}
                <div className="rounded-lg border border-border bg-card p-4 space-y-4">
                  <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                    <FileText className="h-4 w-4" /> Datos de la Factura
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Nº Factura</Label>
                      <Input
                        className="h-8 text-sm bg-secondary border-border"
                        value={activeFile.extractedData.invoice_number || ""}
                        onChange={e => updateInvoiceField(activeFileIndex, "invoice_number", e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Fecha</Label>
                      <Input
                        type="date"
                        className="h-8 text-sm bg-secondary border-border"
                        value={activeFile.extractedData.invoice_date || ""}
                        onChange={e => updateInvoiceField(activeFileIndex, "invoice_date", e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Proveedor</Label>
                      <Input
                        className="h-8 text-sm bg-secondary border-border"
                        value={activeFile.extractedData.supplier_name || ""}
                        onChange={e => updateInvoiceField(activeFileIndex, "supplier_name", e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">CIF/NIF</Label>
                      <Input
                        className="h-8 text-sm bg-secondary border-border"
                        value={activeFile.extractedData.supplier_tax_id || ""}
                        onChange={e => updateInvoiceField(activeFileIndex, "supplier_tax_id", e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Subtotal</Label>
                      <Input
                        type="number" step="0.01"
                        className="h-8 text-sm bg-secondary border-border"
                        value={activeFile.extractedData.subtotal ?? 0}
                        onChange={e => updateInvoiceField(activeFileIndex, "subtotal", parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Envío</Label>
                      <Input
                        type="number" step="0.01"
                        className="h-8 text-sm bg-secondary border-border"
                        value={activeFile.extractedData.shipping_cost ?? 0}
                        onChange={e => updateInvoiceField(activeFileIndex, "shipping_cost", parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Descuento</Label>
                      <Input
                        type="number" step="0.01"
                        className="h-8 text-sm bg-secondary border-border"
                        value={activeFile.extractedData.discount_total ?? 0}
                        onChange={e => updateInvoiceField(activeFileIndex, "discount_total", parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">IVA</Label>
                      <Input
                        type="number" step="0.01"
                        className="h-8 text-sm bg-secondary border-border"
                        value={activeFile.extractedData.tax_amount ?? 0}
                        onChange={e => updateInvoiceField(activeFileIndex, "tax_amount", parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold">Total</Label>
                      <Input
                        type="number" step="0.01"
                        className="h-8 text-sm bg-secondary border-border font-bold"
                        value={activeFile.extractedData.total_amount ?? 0}
                        onChange={e => updateInvoiceField(activeFileIndex, "total_amount", parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  </div>
                </div>

                {/* Missing Equivalence Surcharge block */}
                {!activeFile.extractedData.equivalence_surcharge_amount && (
                  <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 space-y-3">
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                      <Scale className="h-4 w-4" /> Recargo de equivalencia
                    </h3>
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id={`missing-re-${activeFileIndex}`}
                        checked={activeFile.extractedData.missing_equivalence_surcharge || false}
                        onCheckedChange={(checked) => {
                          const isChecked = !!checked;
                          const subtotal = activeFile.extractedData!.subtotal || 0;
                          updateInvoiceFields(activeFileIndex, {
                            missing_equivalence_surcharge: isChecked,
                            missing_surcharge_rate: isChecked ? 5.2 : 0,
                            missing_surcharge_amount: isChecked ? Math.round(subtotal * 0.052 * 100) / 100 : 0,
                          });
                        }}
                        className="mt-0.5"
                      />
                      <div className="flex-1 space-y-1">
                        <Label htmlFor={`missing-re-${activeFileIndex}`} className="text-sm font-medium cursor-pointer">
                          Falta pagar el Recargo de equivalencia del 5,2%
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Marca esta opción cuando el proveedor no haya incluido el recargo de equivalencia en la factura y deba autoliquidarse mediante el modelo 309.
                        </p>
                      </div>
                    </div>
                    {activeFile.extractedData.missing_equivalence_surcharge && (
                      <div className="flex items-center gap-4 pl-7">
                        <div className="space-y-1">
                          <Label className="text-xs">Cuota recargo pendiente</Label>
                          <Input
                            type="number"
                            step="0.01"
                            className="h-8 text-sm bg-secondary border-border w-32 font-semibold"
                            value={activeFile.extractedData.missing_surcharge_amount || 0}
                            readOnly
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Tasa</Label>
                          <Input
                            className="h-8 text-sm bg-muted border-border w-20"
                            value="5,2%"
                            readOnly
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* IVA 309 Expense - Inversión sujeto pasivo / Adquisición intracomunitaria */}
                <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 p-4 space-y-3">
                  <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                    <Scale className="h-4 w-4" /> IVA Modelo 309 — Adq. intracomunitaria / Inversión sujeto pasivo
                  </h3>
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id={`iva-309-${activeFileIndex}`}
                      checked={activeFile.extractedData.iva_309_expense || false}
                      onCheckedChange={(checked) => {
                        const isChecked = !!checked;
                        if (!isChecked) {
                          updateInvoiceFields(activeFileIndex, {
                            iva_309_expense: false,
                            iva_309_mode: "",
                            iva_309_expense_rate: 0,
                            iva_309_expense_amount: 0,
                            iva_309_re_amount: 0,
                          });
                        } else {
                          const subtotal = activeFile.extractedData!.subtotal || 0;
                          updateInvoiceFields(activeFileIndex, {
                            iva_309_expense: true,
                            iva_309_mode: "iva_re",
                            iva_309_expense_rate: 21,
                            iva_309_expense_amount: Math.round(subtotal * 0.21 * 100) / 100,
                            iva_309_re_amount: Math.round(subtotal * 0.052 * 100) / 100,
                          });
                        }
                      }}
                      className="mt-0.5"
                    />
                    <div className="flex-1 space-y-1">
                      <Label htmlFor={`iva-309-${activeFileIndex}`} className="text-sm font-medium cursor-pointer">
                        Autorrepercusión Modelo 309
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Para bienes adquiridos en operaciones intracomunitarias o con inversión del sujeto pasivo.
                      </p>
                    </div>
                  </div>
                  {activeFile.extractedData.iva_309_expense && (
                    <div className="space-y-3 pl-7">
                      <RadioGroup
                        value={activeFile.extractedData.iva_309_mode || "iva_re"}
                        onValueChange={(value: string) => {
                          const subtotal = activeFile.extractedData!.subtotal || 0;
                          updateInvoiceFields(activeFileIndex, {
                            iva_309_mode: value,
                            iva_309_expense_amount: Math.round(subtotal * 0.21 * 100) / 100,
                            iva_309_re_amount: value === "iva_re" ? Math.round(subtotal * 0.052 * 100) / 100 : 0,
                          });
                        }}
                        className="flex flex-col gap-2"
                      >
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="iva_re" id={`mode-iva-re-${activeFileIndex}`} />
                          <Label htmlFor={`mode-iva-re-${activeFileIndex}`} className="text-sm cursor-pointer">Autorrepercutir IVA (21%) + RE (5,2%)</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="solo_iva" id={`mode-solo-iva-${activeFileIndex}`} />
                          <Label htmlFor={`mode-solo-iva-${activeFileIndex}`} className="text-sm cursor-pointer">Autorrepercutir solo IVA (21%)</Label>
                        </div>
                      </RadioGroup>
                      <div className="flex items-center gap-4 flex-wrap">
                        <div className="space-y-1">
                          <Label className="text-xs">IVA autorrepercutido</Label>
                          <Input
                            type="number"
                            step="0.01"
                            className="h-8 text-sm bg-secondary border-border w-32 font-semibold"
                            value={activeFile.extractedData.iva_309_expense_amount || 0}
                            onChange={(e) => updateInvoiceField(activeFileIndex, "iva_309_expense_amount", parseFloat(e.target.value) || 0)}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Tasa IVA</Label>
                          <Input className="h-8 text-sm bg-muted border-border w-20" value="21%" readOnly />
                        </div>
                        {(activeFile.extractedData.iva_309_mode || "iva_re") === "iva_re" && (
                          <>
                            <div className="space-y-1">
                              <Label className="text-xs">RE autorrepercutido</Label>
                              <Input
                                type="number"
                                step="0.01"
                                className="h-8 text-sm bg-secondary border-border w-32 font-semibold"
                                value={activeFile.extractedData.iva_309_re_amount || 0}
                                onChange={(e) => updateInvoiceField(activeFileIndex, "iva_309_re_amount", parseFloat(e.target.value) || 0)}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Tasa RE</Label>
                              <Input className="h-8 text-sm bg-muted border-border w-20" value="5,2%" readOnly />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Products list */}
                <div className="rounded-lg border border-border bg-card p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                      <Package className="h-4 w-4" /> Productos detectados ({activeFile.extractedData.items.length})
                    </h3>
                  </div>

                  {activeFile.extractedData.items.length === 0 ? (
                    <div className="text-center py-6 text-muted-foreground">
                      <AlertTriangle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No se detectaron productos en esta factura</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {activeFile.extractedData.items.map((item, idx) => (
                        <div key={idx} className={cn(
                          "rounded-lg border p-3 space-y-2 transition-colors",
                          item.selected ? "border-border bg-secondary/30" : "border-border/50 bg-muted/20 opacity-60"
                        )}>
                          <div className="flex items-start gap-3">
                            <Checkbox
                              checked={item.selected}
                              onCheckedChange={(checked) => updateItem(activeFileIndex, idx, "selected", !!checked)}
                              className="mt-1"
                            />
                            <div className="flex-1 min-w-0 space-y-2">
                              <div className="space-y-1">
                                <Label className="text-xs">Nombre del producto</Label>
                                <Input
                                  className="h-8 text-sm bg-secondary border-border"
                                  value={item.description}
                                  onChange={e => updateItem(activeFileIndex, idx, "description", e.target.value)}
                                  disabled={!item.selected}
                                />
                              </div>
                              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                                <div className="space-y-1">
                                  <Label className="text-xs">SKU</Label>
                                  <Input
                                    className="h-7 text-xs bg-secondary border-border"
                                    placeholder="Auto"
                                    value={item.sku || ""}
                                    onChange={e => updateItem(activeFileIndex, idx, "sku", e.target.value)}
                                    disabled={!item.selected}
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Cantidad</Label>
                                  <Input
                                    type="number" min="1"
                                    className="h-7 text-xs bg-secondary border-border"
                                    value={item.quantity}
                                    onChange={e => updateItem(activeFileIndex, idx, "quantity", parseInt(e.target.value) || 1)}
                                    disabled={!item.selected}
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Precio Unit.</Label>
                                  <Input
                                    type="number" step="0.01"
                                    className="h-7 text-xs bg-secondary border-border"
                                    value={item.unit_price}
                                    onChange={e => updateItem(activeFileIndex, idx, "unit_price", parseFloat(e.target.value) || 0)}
                                    disabled={!item.selected}
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Descuento</Label>
                                  <Input
                                    type="number" step="0.01"
                                    className="h-7 text-xs bg-secondary border-border"
                                    value={item.discount || 0}
                                    onChange={e => updateItem(activeFileIndex, idx, "discount", parseFloat(e.target.value) || 0)}
                                    disabled={!item.selected}
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs font-semibold">Total línea</Label>
                                  <Input
                                    type="number" step="0.01"
                                    className="h-7 text-xs bg-secondary border-border font-semibold"
                                    value={item.line_total}
                                    onChange={e => updateItem(activeFileIndex, idx, "line_total", parseFloat(e.target.value) || 0)}
                                    disabled={!item.selected}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {activeFile?.status === "error" && (
              <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-4 text-center">
                <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-destructive" />
                <p className="text-sm font-medium">Error procesando este archivo</p>
                <p className="text-xs text-muted-foreground mt-1">{activeFile.error}</p>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setStep("upload")}>Volver</Button>
              <Button
                onClick={createOrdersFromInvoices}
                disabled={!hasProcessedFiles}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Crear {completedCount} orden{completedCount !== 1 ? "es" : ""} y productos
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Step: Creating */}
        {step === "creating" && (
          <div className="space-y-6 py-8">
            <div className="text-center">
              <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
              <h3 className="text-lg font-semibold">Creando órdenes y productos...</h3>
              <p className="text-sm text-muted-foreground mt-1">
                No cierres esta ventana
              </p>
            </div>
            <Progress value={creationProgress} className="w-full" />
            <p className="text-center text-sm text-muted-foreground">{creationProgress}% completado</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
